import { type UIEvent, useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  FileAttachmentIcon,
  ImageLightbox,
  type ImageLightboxItem,
  fileKindLabel,
  isImageAttachment,
} from './AttachmentPreview.tsx';
import { useFeatures } from '../context/FeatureFlagContext.tsx';

const TERMINAL_TURN_STATUSES = new Set([
  'Done',
  'Failed',
  'Canceled',
  'Cancelled',
]);

// Marker prepended by UI for extended context models - strip when displaying.
// Handle various newline formats and multiple occurrences.
const EXTENDED_CONTEXT_MARKER_REGEX = /^\[\[EXTENDED_CONTEXT:1M\]\]\r?\n?/gm;

function stripExtendedContextMarker(text: string): string {
  return text.replace(EXTENDED_CONTEXT_MARKER_REGEX, "");
}

type PendingCommand = {
  name: string;
  command?: string;
  call_id?: string;
  variant?: 'setup';
  filePath?: string;
  pattern?: string;
};

type AttachmentRenderItem = {
  type: 'attachment';
  fileId: string;
  fileName: string;
  contentType: string;
  downloadUrl: string;
};

type AttachmentErrorRenderItem = {
  type: 'attachment_error';
  fileName?: string;
  error: string;
};

type RenderItem =
  | { type: 'message'; text: string; role?: string }
  | { type: 'reasoning'; title: string; text: string }
  | {
      type: 'terminal';
      name: string;
      command: string;
      output: string;
      call_id?: string;
      variant?: 'setup';
    }
  | { type: 'tool_invocation'; name: string; output: string; call_id?: string }
  | {
      type: 'file_tool';
      name: string;
      filePath: string;
      output: string;
      call_id?: string;
    }
  | {
      type: 'search_tool';
      name: string;
      pattern: string;
      output: string;
      call_id?: string;
    }
  | AttachmentRenderItem
  | AttachmentErrorRenderItem;

interface SSELog {
  Timestamp?: string;
  Message?: string;
}

export function processLogLine(
  line: string,
  pendingCmds: Map<string, PendingCommand>,
  options?: { enableAttachments?: boolean }
): RenderItem[] {
  const items: RenderItem[] = [];
  const enableAttachments = options?.enableAttachments ?? false;
  try {
    const obj = JSON.parse(line);
    switch (obj.type) {
      case 'message': {
        let txt = obj.content?.map((c: any) => c.text).join('\n') || '';
        // Strip extended context marker from user messages.
        if (obj.role === 'user') {
          txt = stripExtendedContextMarker(txt);
        }
        items.push({ type: 'message', text: txt, role: obj.role });
        break;
      }
      case 'reasoning': {
        const summary = obj.summary?.find((s: any) => s.type === 'summary_text');
        if (summary) {
          let title = '';
          let text = summary.text;
          const m = text.match(/^\s*\*\*([^*]+)\*\*\s*(?:\r?\n\n)?([\s\S]*)/);
          if (m) {
            title = m[1].trim();
            text = m[2];
          }
          items.push({ type: 'reasoning', title, text });
        }
        break;
      }
      case 'attachment': {
        if (!enableAttachments) {
          break;
        }
        if (
          typeof obj.file_id === 'string' &&
          typeof obj.file_name === 'string' &&
          typeof obj.content_type === 'string'
        ) {
          items.push({
            type: 'attachment',
            fileId: obj.file_id,
            fileName: obj.file_name,
            contentType: obj.content_type,
            downloadUrl: `/files/${encodeURIComponent(obj.file_id)}`,
          });
        }
        break;
      }
      case 'attachment_error': {
        if (!enableAttachments) {
          break;
        }
        let error = '';
        if (typeof obj.error === 'string') {
          error = obj.error;
        } else if (typeof obj.message === 'string') {
          error = obj.message;
        } else if (typeof obj.reason === 'string') {
          error = obj.reason;
        }
        if (error) {
          items.push({
            type: 'attachment_error',
            fileName: typeof obj.file_name === 'string' ? obj.file_name : undefined,
            error,
          });
        }
        break;
      }
      case 'function_call': {
        const name = obj.name || 'function';
        const pending: PendingCommand = { name, call_id: obj.call_id };
        try {
          const args = JSON.parse(obj.arguments);
          if (name === 'shell') {
            if (Array.isArray(args.command)) {
              pending.command = args.command.join(' ');
            } else {
              pending.command = String(args.command);
            }
          } else if (name === 'file_read' || name === 'apply_patch') {
            pending.filePath = args.path || args.file_path || '';
          } else if (name === 'list_dir') {
            pending.filePath = args.path || args.directory || '';
          } else if (name === 'file_glob' || name === 'file_grep') {
            pending.pattern = args.pattern || '';
          }
        } catch {
          // ignore parse errors
        }
        if (obj.call_id) {
          pendingCmds.set(obj.call_id, pending);
        }
        break;
      }
      case 'function_call_output': {
        let out = '';
        if (typeof obj.output === 'string') {
          try {
            const parsed = JSON.parse(obj.output);
            out = parsed.output ?? obj.output;
          } catch {
            out = obj.output;
          }
        } else if (Array.isArray(obj.output)) {
          out = obj.output.map((o: any) => o.output).join('');
        }
        const callId = obj.call_id;
        const pending = callId ? pendingCmds.get(callId) : undefined;
        if (pending) {
          const name = pending.name;
          if (name === 'shell') {
            items.push({
              type: 'terminal',
              name,
              command: pending.command || '',
              output: out,
              call_id: callId,
              variant: pending.variant,
            });
          } else if (name === 'file_read' || name === 'apply_patch' || name === 'list_dir') {
            items.push({
              type: 'file_tool',
              name,
              filePath: pending.filePath || '',
              output: out,
              call_id: callId,
            });
          } else if (name === 'file_glob' || name === 'file_grep') {
            items.push({
              type: 'search_tool',
              name,
              pattern: pending.pattern || '',
              output: out,
              call_id: callId,
            });
          } else {
            items.push({ type: 'tool_invocation', name, output: out, call_id: callId });
          }
          pendingCmds.delete(callId);
        }
        break;
      }
      default:
        // ignore unknown log entries
        break;
    }
  } catch {
    // ignore lines that cannot be parsed
  }
  return items;
}

export default function LogView({
  taskID,
  turnID,
  workstreamID,
}: {
  taskID?: string;
  turnID?: string;
  workstreamID?: string;
}) {
  const features = useFeatures();
  const attachmentsEnabled = features['NewAgent'];
  const wsQuery = workstreamID ? `?workstreamID=${encodeURIComponent(workstreamID)}` : '';
  const [logs, setLogs] = useState<RenderItem[]>([]);
  const pendingCmdsRef = useRef<Map<string, PendingCommand>>(new Map());
  const decodersRef = useRef<Map<string, TextDecoder>>(new Map());
  const itemIndexRef = useRef<Map<string, number>>(new Map());
  const logsRef = useRef<RenderItem[]>([]);
  const [streamTurnID, setStreamTurnID] = useState<string | undefined>(turnID);
  const statusPollRef = useRef<number | null>(null);
  const [turnStatus, setTurnStatus] = useState<string | undefined>(undefined);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [lightboxItem, setLightboxItem] = useState<ImageLightboxItem | null>(null);

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    window.requestAnimationFrame(() => {
      const node = scrollContainerRef.current;
      if (!node) {
        return;
      }
      node.scrollTop = node.scrollHeight;
    });
  }, []);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const distanceFromBottom =
        target.scrollHeight - (target.scrollTop + target.clientHeight);
      const atBottom = distanceFromBottom <= 8;
      setAutoScrollEnabled((prev) => (prev === atBottom ? prev : atBottom));
    },
    [],
  );

  useEffect(() => {
    setAutoScrollEnabled(true);
    scrollToBottom();
  }, [taskID, turnID, scrollToBottom]);

  useEffect(() => {
    setAutoScrollEnabled(true);
    scrollToBottom();
  }, [streamTurnID, scrollToBottom]);

  useEffect(() => {
    if (!autoScrollEnabled) {
      return;
    }
    scrollToBottom();
  }, [logs, autoScrollEnabled, scrollToBottom]);

  const stopStatusPolling = () => {
    if (statusPollRef.current !== null) {
      window.clearInterval(statusPollRef.current);
      statusPollRef.current = null;
    }
  };

  useEffect(() => {
    pendingCmdsRef.current.clear();
    setLogs([]);
    logsRef.current = [];
    itemIndexRef.current.clear();
    decodersRef.current.clear();
    setStreamTurnID(turnID);
    setTurnStatus(undefined);
    stopStatusPolling();

    if (!taskID) return;

    const abort = new AbortController();
    let eventSource: EventSource | null = null;
    let currentTurnID: string | undefined;

    const fetchTurnStatus = async (turn: string) => {
      try {
        const resp = await fetch(`/api/tasks/${taskID}/turns/${turn}${wsQuery}`, {
          signal: abort.signal,
        });
        if (!resp.ok) {
          return;
        }
        const data = await resp.json();
        if (abort.signal.aborted) {
          return;
        }
        const status: string | undefined = data?.Status;
        if (status !== undefined) {
          setTurnStatus((prev) => (prev === status ? prev : status));
          if (TERMINAL_TURN_STATUSES.has(status)) {
            stopStatusPolling();
          }
        }
      } catch (err) {
        if (!abort.signal.aborted) {
          console.error('failed to fetch turn status', err);
        }
      }
    };

    const startStatusPolling = (
      turn: string,
      options?: { skipImmediate?: boolean }
    ) => {
      if (statusPollRef.current !== null) {
        return;
      }
      const poll = () => {
        void fetchTurnStatus(turn);
      };
      statusPollRef.current = window.setInterval(poll, 3000);
      if (!options?.skipImmediate) {
        poll();
      }
    };

    function base64ToBytes(b64: string): Uint8Array {
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      return arr;
    }

    function appendText(callId: string, text: string) {
      const idx = itemIndexRef.current.get(callId);
      if (idx === undefined) return;
      const list = [...logsRef.current];
      const item = list[idx];
      if (
        item.type === 'terminal' ||
        item.type === 'tool_invocation' ||
        item.type === 'file_tool' ||
        item.type === 'search_tool'
      ) {
        item.output += text;
      } else if (item.type === 'message') {
        item.text += text;
      } else if (item.type === 'reasoning') {
        item.text += text;
      }
      logsRef.current = list;
      setLogs(list);
    }

    function handleChunk(obj: any) {
      const callId = obj.call_id || '_';
      let decoder = decodersRef.current.get(callId);
      if (!decoder) {
        decoder = new TextDecoder();
        decodersRef.current.set(callId, decoder);
      }
      const bytes = base64ToBytes(obj.base64 || '');
      const text = decoder.decode(bytes, { stream: !obj.eof });
      if (text) {
        if (!itemIndexRef.current.has(callId)) {
          let newItem: RenderItem;
          const pending = pendingCmdsRef.current.get(callId);
          if (pending) {
            const name = pending.name;
            if (name === 'shell') {
              newItem = {
                type: 'terminal',
                name,
                command: pending.command || '',
                output: text,
                call_id: callId,
                variant: pending.variant,
              };
            } else if (name === 'file_read' || name === 'apply_patch' || name === 'list_dir') {
              newItem = {
                type: 'file_tool',
                name,
                filePath: pending.filePath || '',
                output: text,
                call_id: callId,
              };
            } else if (name === 'file_glob' || name === 'file_grep') {
              newItem = {
                type: 'search_tool',
                name,
                pattern: pending.pattern || '',
                output: text,
                call_id: callId,
              };
            } else {
              newItem = {
                type: 'tool_invocation',
                name,
                output: text,
                call_id: callId,
              };
            }
            pendingCmdsRef.current.delete(callId);
          } else {
            newItem = { type: 'message', text, role: 'assistant' };
          }
          const idx = logsRef.current.length;
          itemIndexRef.current.set(callId, idx);
          const newLogs = [...logsRef.current, newItem];
          logsRef.current = newLogs;
          setLogs(newLogs);
        } else {
          appendText(callId, text);
        }
      }
      if (obj.eof) {
        const flush = decoder.decode();
        if (flush) appendText(callId, flush);
        decodersRef.current.delete(callId);
        itemIndexRef.current.delete(callId);
        pendingCmdsRef.current.delete(callId);
      }
    }

    async function start() {
      try {
        let targetTurnID = turnID;
        let initialStatus: string | undefined;
        let skipImmediatePoll = false;
        if (!targetTurnID) {
          const resp = await fetch(
            `/api/tasks/${taskID}/turns${wsQuery ? wsQuery + '&' : '?'}maxResults=1`,
            { signal: abort.signal }
          );
          if (!resp.ok) return;
          const data = await resp.json();
          const latest = data?.Turns?.[0];
          targetTurnID = latest?.TurnID;
          initialStatus = latest?.Status;
          if (initialStatus !== undefined) {
            skipImmediatePoll = true;
          }
        } else {
          try {
            const resp = await fetch(
              `/api/tasks/${taskID}/turns/${targetTurnID}${wsQuery}`,
              { signal: abort.signal }
            );
            if (resp.ok) {
              const data = await resp.json();
              initialStatus = data?.Status;
              if (initialStatus !== undefined) {
                skipImmediatePoll = true;
              }
            }
          } catch (err) {
            if (!abort.signal.aborted) {
              console.error('failed to load turn details', err);
            }
          }
        }
        if (!targetTurnID || abort.signal.aborted) return;

        currentTurnID = targetTurnID;
        setStreamTurnID(targetTurnID);
        setTurnStatus(initialStatus);
        if (!initialStatus || !TERMINAL_TURN_STATUSES.has(initialStatus)) {
          startStatusPolling(targetTurnID, {
            skipImmediate: skipImmediatePoll,
          });
        }

        eventSource = new EventSource(
          `/api/tasks/${taskID}/turns/${targetTurnID}/logs${wsQuery}`
        );
        const handleLogEvent = (ev: MessageEvent) => {
          if (abort.signal.aborted) return;
          try {
            const sse: SSELog = JSON.parse(ev.data);
            if (!sse.Message) return;
            const obj = JSON.parse(sse.Message);
            // Handle Event Horizon specific streaming events
            if (obj.type === 'event-horizon/chunk') {
              handleChunk(obj);
              return;
            }
            // New style setup-env events
            if (obj.type === 'event-horizon/start_setup_env') {
              // Pretend this is the beginning of a shell invocation so that
              // subsequent chunks are rendered inside a terminal window.
              if (obj.call_id) {
                pendingCmdsRef.current.set(obj.call_id, {
                  name: 'shell',
                  command: obj.command ?? '',
                  call_id: obj.call_id,
                  variant: 'setup',
                });
                if (!itemIndexRef.current.has(obj.call_id)) {
                  const initialItem: RenderItem = {
                    type: 'terminal',
                    name: 'shell',
                    command: obj.command ?? '',
                    output: 'Running environment setup script...\n',
                    call_id: obj.call_id,
                    variant: 'setup',
                  };
                  const idx = logsRef.current.length;
                  itemIndexRef.current.set(obj.call_id, idx);
                  const newLogs = [...logsRef.current, initialItem];
                  logsRef.current = newLogs;
                  setLogs(newLogs);
                }
              }
              return;
            }
            if (obj.type === 'event-horizon/end_setup_env') {
              // Nothing special to do – once the final chunk with eof=true is
              // received the decoder gets flushed and closed.
              if (obj.call_id) {
                pendingCmdsRef.current.delete(obj.call_id);
              }
              return;
            }
            const items = processLogLine(sse.Message, pendingCmdsRef.current, {
              enableAttachments: attachmentsEnabled,
            });
            if (items.length) {
              let idx = logsRef.current.length;
              for (const it of items) {
                if (
                  (it.type === 'terminal' ||
                    it.type === 'tool_invocation' ||
                    it.type === 'file_tool' ||
                    it.type === 'search_tool') &&
                  it.call_id
                ) {
                  itemIndexRef.current.set(it.call_id, idx);
                }
                idx++;
              }
              const newLogs = [...logsRef.current, ...items];
              logsRef.current = newLogs;
              setLogs(newLogs);
            }
          } catch (err) {
            console.error('failed to parse event data', err);
          }
        };
        eventSource.addEventListener('log', handleLogEvent);
        eventSource.onerror = (err) => {
          if (abort.signal.aborted) return;
          const source = err.target as EventSource | null;
          const shouldLog = !source || source.readyState !== EventSource.CLOSED;
          eventSource?.close();
          if (shouldLog) {
            console.error('log stream error', err);
          }
          if (currentTurnID) {
            void fetchTurnStatus(currentTurnID);
          }
        };
      } catch (err) {
        if (!abort.signal.aborted) {
          console.error('failed to start log stream', err);
        }
      }
    }

    start();

    return () => {
      abort.abort();
      eventSource?.close();
      stopStatusPolling();
    };
  }, [taskID, turnID, wsQuery]);

  const hasLogs = logs.length > 0;
  const isTurnActive = Boolean(
    streamTurnID && (!turnStatus || !TERMINAL_TURN_STATUSES.has(turnStatus))
  );

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        textAlign: 'left',
        boxSizing: 'border-box',
      }}
    >
      <h2>
        Logs
        {streamTurnID ? ` – Turn ${streamTurnID}` : ''}
      </h2>
      <div style={{ fontFamily: 'monospace' }}>
      {!streamTurnID && (
        <div
          style={{
            fontStyle: 'italic',
            color: '#666666',
            marginTop: '8px',
            width: '800px',
          }}
        >
          Select a turn to view logs.
        </div>
      )}
      {streamTurnID && !hasLogs && (
        <div
          style={{
            fontStyle: 'italic',
            color: '#666666',
            marginTop: '8px',
            width: '800px',
          }}
        >
          Logs will appear here as they are received.
        </div>
      )}
      {hasLogs &&
        logs.map((l, idx) => {
          const setupIconTitleId = `setup-icon-title-${idx}`;
          const setupIconDescId = `setup-icon-desc-${idx}`;
          const setupIconMaskId = `setup-icon-mask-${idx}`;
          return (
            <div key={idx} style={{ marginBottom: '1em', width: '800px' }}>
              {l.type === 'message' && (
                l.role === 'user' ? (
                  <pre
                    style={{
                      background: '#d1dff6',
                      padding: '8px',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                      marginBottom: '4px',
                    }}
                  >
                    {l.text}
                  </pre>
                ) : features['NewAgent'] ? (
                  <div
                    className="markdown-body"
                    style={{
                      background: '#ffffff',
                      padding: '16px',
                      borderRadius: '6px',
                      marginBottom: '4px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif',
                      fontSize: '14px',
                      lineHeight: 1.5,
                      color: '#1f2328',
                    }}
                  >
                    <ReactMarkdown
                      components={{
                        code: ({ className, children, ...props }) => {
                          const isInline = !className;
                          if (isInline) {
                            return (
                              <code
                                style={{
                                  backgroundColor: 'rgba(175, 184, 193, 0.2)',
                                  padding: '0.2em 0.4em',
                                  borderRadius: '6px',
                                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                                  fontSize: '85%',
                                  whiteSpace: 'break-spaces',
                                }}
                                {...props}
                              >
                                {children}
                              </code>
                            );
                          }
                          return (
                            <code
                              className={className}
                              style={{
                                display: 'block',
                                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                                fontSize: '85%',
                                whiteSpace: 'pre',
                              }}
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        },
                        pre: ({ children }) => (
                          <pre
                            style={{
                              backgroundColor: '#f6f8fa',
                              padding: '16px',
                              borderRadius: '6px',
                              overflow: 'auto',
                              margin: '16px 0',
                              fontSize: '85%',
                              lineHeight: 1.45,
                            }}
                          >
                            {children}
                          </pre>
                        ),
                        p: ({ children }) => (
                          <p style={{ margin: 0 }}>
                            {children}
                          </p>
                        ),
                        ul: ({ children }) => (
                          <ul style={{ margin: '0 0 16px 0', paddingLeft: '2em' }}>
                            {children}
                          </ul>
                        ),
                        ol: ({ children }) => (
                          <ol style={{ margin: '0 0 16px 0', paddingLeft: '2em' }}>
                            {children}
                          </ol>
                        ),
                        li: ({ children }) => (
                          <li style={{ marginTop: '4px' }}>{children}</li>
                        ),
                        strong: ({ children }) => (
                          <strong style={{ fontWeight: 600 }}>{children}</strong>
                        ),
                        a: ({ href, children }) => (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#0969da', textDecoration: 'none' }}
                          >
                            {children}
                          </a>
                        ),
                        h1: ({ children }) => (
                          <h1 style={{ fontSize: '2em', fontWeight: 600, margin: '24px 0 16px 0', paddingBottom: '0.3em', borderBottom: '1px solid #d1d9e0' }}>
                            {children}
                          </h1>
                        ),
                        h2: ({ children }) => (
                          <h2 style={{ fontSize: '1.5em', fontWeight: 600, margin: '24px 0 16px 0', paddingBottom: '0.3em', borderBottom: '1px solid #d1d9e0' }}>
                            {children}
                          </h2>
                        ),
                        h3: ({ children }) => (
                          <h3 style={{ fontSize: '1.25em', fontWeight: 600, margin: '24px 0 16px 0' }}>
                            {children}
                          </h3>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote
                            style={{
                              margin: '0 0 16px 0',
                              padding: '0 1em',
                              color: '#656d76',
                              borderLeft: '0.25em solid #d1d9e0',
                            }}
                          >
                            {children}
                          </blockquote>
                        ),
                        hr: () => (
                          <hr style={{ height: '0.25em', padding: 0, margin: '24px 0', backgroundColor: '#d1d9e0', border: 0 }} />
                        ),
                      }}
                    >
                      {l.text}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div
                    style={{
                      background: '#ffffff',
                      padding: '8px',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      marginBottom: '4px',
                      fontFamily: 'serif',
                    }}
                  >
                    {l.text}
                  </div>
                )
              )}
              {l.type === 'attachment' && (
                <AttachmentCard
                  item={l}
                  onOpenImage={(item) => setLightboxItem(item)}
                />
              )}
              {l.type === 'attachment_error' && (
                <AttachmentErrorCard item={l} />
              )}
              {l.type === 'reasoning' && (
                <div style={{ marginBottom: '4px', fontFamily: 'serif' }}>
                  <div
                    style={{
                      background: '#d3d3d3',
                      color: '#222222',
                      fontWeight: 'bold',
                      padding: '8px',
                      borderTopLeftRadius: '4px',
                      borderTopRightRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5em',
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 512 560"
                      fill="white"
                      stroke="black"
                      strokeWidth="20"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    >
                      <path d="M 375 80 Q 360 44 328 22 Q 297   1 256   0 Q 215   1 184  22 Q 152  44 137  80 Q 133  80 128  80 Q  74  81  37 117 Q   1 154   0 208 Q   1 262  37 299 Q  74 335 128 336 Q 150 336 169 329 Q 181 354 204 369 Q 227 384 256 384 Q 285 384 308 369 Q 331 354 343 329 Q 362 336 384 336 Q 438 335 475 299 Q 511 262 512 208 Q 511 154 475 117 Q 438  81 384  80 Q 379  80 375  80 Z" />
                      <circle cx="160" cy="460" r="45" />
                      <circle cx="80" cy="535" r="30" />
                    </svg>
                    {l.title}
                  </div>
                  <div
                    style={{
                      background: '#e8e8e8',
                      padding: '8px',
                      borderBottomLeftRadius: '4px',
                      borderBottomRightRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {l.text}
                  </div>
                </div>
              )}
              {l.type === 'terminal' && (
                <div style={{ marginBottom: '4px' }}>
                  <div
                    style={{
                      background: '#2b2b2b',
                      color: '#d4d4d4',
                      fontWeight: 'bold',
                      padding: '8px',
                      paddingBottom: '8px',
                      borderTopLeftRadius: '4px',
                      borderTopRightRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5em',
                    }}
                  >
                    {l.variant === 'setup' ? (
                      <>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          role="img"
                          aria-labelledby={`${setupIconTitleId} ${setupIconDescId}`}
                        >
                          <title id={setupIconTitleId}>White Gear Icon</title>
                          <desc id={setupIconDescId}>
                            Eight-tooth white gear with center hole
                          </desc>

                          <mask id={setupIconMaskId}>
                            <rect width="24" height="24" fill="#fff" />
                            <circle cx="12" cy="12" r="3.25" fill="#000" />
                          </mask>

                          <g fill="#fff" mask={`url(#${setupIconMaskId})`}>
                            <circle cx="12" cy="12" r="7" />
                            <rect
                              x="10.5"
                              y="1.6"
                              width="3"
                              height="4"
                              rx="0.8"
                              transform="rotate(0 12 12)"
                            />
                            <rect
                              x="10.5"
                              y="1.6"
                              width="3"
                              height="4"
                              rx="0.8"
                              transform="rotate(45 12 12)"
                            />
                            <rect
                              x="10.5"
                              y="1.6"
                              width="3"
                              height="4"
                              rx="0.8"
                              transform="rotate(90 12 12)"
                            />
                            <rect
                              x="10.5"
                              y="1.6"
                              width="3"
                              height="4"
                              rx="0.8"
                              transform="rotate(135 12 12)"
                            />
                            <rect
                              x="10.5"
                              y="1.6"
                              width="3"
                              height="4"
                              rx="0.8"
                              transform="rotate(180 12 12)"
                            />
                            <rect
                              x="10.5"
                              y="1.6"
                              width="3"
                              height="4"
                              rx="0.8"
                              transform="rotate(225 12 12)"
                            />
                            <rect
                              x="10.5"
                              y="1.6"
                              width="3"
                              height="4"
                              rx="0.8"
                              transform="rotate(270 12 12)"
                            />
                            <rect
                              x="10.5"
                              y="1.6"
                              width="3"
                              height="4"
                              rx="0.8"
                              transform="rotate(315 12 12)"
                            />
                          </g>
                        </svg>
                        Configuring Environment
                      </>
                    ) : (
                      <>
                        {l.name === 'shell' && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            aria-labelledby="title"
                            role="img"
                          >
                            <title id="title">Terminal Icon</title>
                            <rect
                              x="1"
                              y="1"
                              width="22"
                              height="22"
                              rx="2"
                              ry="2"
                              fill="none"
                              stroke="white"
                              strokeWidth="2"
                            />
                            <polyline
                              points="6 8 10 12 6 16"
                              fill="none"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <line
                              x1="12"
                              y1="16"
                              x2="18"
                              y2="16"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                        {l.name}
                      </>
                    )}
                  </div>
                  <pre
                    style={{
                      background: '#1e1e1e',
                      color: '#d4d4d4',
                      padding: '0 8px 8px 8px',
                      paddingTop: '1em',
                      marginTop: '0',
                      borderBottomLeftRadius: '4px',
                      borderBottomRightRadius: '4px',
                      overflowX: 'auto',
                    }}
                  >
                    {l.command}
                    {l.command ? '\n' : ''}
                    {l.output}
                  </pre>
                </div>
              )}
              {l.type === 'file_tool' && (
                features['NewAgent'] ? (
                  <div style={{ marginBottom: '4px' }}>
                    <div
                      style={{
                        background: '#1a365d',
                        color: '#e2e8f0',
                        fontWeight: 'bold',
                        padding: '8px',
                        borderTopLeftRadius: '4px',
                        borderTopRightRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5em',
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                      </svg>
                      <span>{l.name}</span>
                      {l.filePath && (
                        <span style={{ fontWeight: 'normal', opacity: 0.8, marginLeft: '0.5em' }}>
                          {l.filePath}
                        </span>
                      )}
                    </div>
                    <pre
                      style={{
                        background: '#0d1b2a',
                        color: '#a0c4ff',
                        padding: '8px',
                        margin: 0,
                        borderBottomLeftRadius: '4px',
                        borderBottomRightRadius: '4px',
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {l.output}
                    </pre>
                  </div>
                ) : (
                  <div
                    style={{
                      background: '#f0f0f0',
                      padding: '8px',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      marginBottom: '4px',
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{l.name}</div>
                    {l.output}
                  </div>
                )
              )}
              {l.type === 'search_tool' && (
                features['NewAgent'] ? (
                  <div style={{ marginBottom: '4px' }}>
                    <div
                      style={{
                        background: '#065f46',
                        color: '#d1fae5',
                        fontWeight: 'bold',
                        padding: '8px',
                        borderTopLeftRadius: '4px',
                        borderTopRightRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5em',
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <span>{l.name}</span>
                      {l.pattern && (
                        <span
                          style={{
                            fontWeight: 'normal',
                            fontFamily: 'monospace',
                            background: 'rgba(255,255,255,0.15)',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            marginLeft: '0.5em',
                          }}
                        >
                          {l.pattern}
                        </span>
                      )}
                    </div>
                    <pre
                      style={{
                        background: '#022c22',
                        color: '#6ee7b7',
                        padding: '8px',
                        margin: 0,
                        borderBottomLeftRadius: '4px',
                        borderBottomRightRadius: '4px',
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {l.output}
                    </pre>
                  </div>
                ) : (
                  <div
                    style={{
                      background: '#f0f0f0',
                      padding: '8px',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      marginBottom: '4px',
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{l.name}</div>
                    {l.output}
                  </div>
                )
              )}
              {l.type === 'tool_invocation' && (
                <div
                  style={{
                    background: '#f0f0f0',
                    padding: '8px',
                    borderRadius: '4px',
                    whiteSpace: 'pre-wrap',
                    marginBottom: '4px',
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{l.name}</div>
                  {l.output}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {isTurnActive && (
        <div
          className="log-view-activity-indicator"
          role="status"
          aria-live="polite"
          style={{ width: '800px' }}
        >
          <span>Task running</span>
          <span className="log-view-activity-dots" aria-hidden="true">
            <span className="log-view-activity-dot" />
            <span className="log-view-activity-dot" />
            <span className="log-view-activity-dot" />
          </span>
        </div>
      )}
      {attachmentsEnabled && lightboxItem && (
        <ImageLightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />
      )}
    </div>
  );
}

export function AttachmentCard({
  item,
  onOpenImage,
}: {
  item: AttachmentRenderItem;
  onOpenImage: (item: ImageLightboxItem) => void;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const isImage = isImageAttachment(item.contentType);
  const showThumbnail = isImage && !imageFailed;

  if (showThumbnail) {
    return (
      <button
        type="button"
        onClick={() =>
          onOpenImage({
            fileName: item.fileName,
            downloadUrl: item.downloadUrl,
          })
        }
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          width: '100%',
          padding: '12px',
          marginBottom: '4px',
          background: '#f8fafc',
          border: '1px solid #dbe4ee',
          borderRadius: '8px',
          color: '#0f172a',
          textAlign: 'left',
          cursor: 'zoom-in',
        }}
        aria-label={`Open image attachment ${item.fileName}`}
      >
        <img
          src={item.downloadUrl}
          alt={item.fileName}
          onError={() => setImageFailed(true)}
          style={{
            width: '72px',
            height: '72px',
            objectFit: 'cover',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            background: '#ffffff',
            flexShrink: 0,
          }}
        />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'system-ui, sans-serif',
              fontWeight: 600,
              marginBottom: '4px',
              overflowWrap: 'anywhere',
            }}
          >
            {item.fileName}
          </div>
          <div
            style={{
              color: '#475569',
              fontFamily: 'system-ui, sans-serif',
              fontSize: '13px',
            }}
          >
            {item.contentType}
          </div>
        </div>
      </button>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px',
        marginBottom: '4px',
        background: '#f8fafc',
        border: '1px solid #dbe4ee',
        borderRadius: '8px',
        color: '#0f172a',
      }}
    >
      <FileAttachmentIcon label={fileKindLabel(item.contentType)} />
      <div style={{ minWidth: 0 }}>
        <a
          href={item.downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="attachment-file-link"
          style={{
            fontFamily: 'system-ui, sans-serif',
            fontWeight: 600,
            marginBottom: '4px',
            overflowWrap: 'anywhere',
            display: 'block',
          }}
        >
          {item.fileName}
        </a>
        <div
          style={{
            color: '#475569',
            fontFamily: 'system-ui, sans-serif',
            fontSize: '13px',
          }}
        >
          {item.contentType}
        </div>
      </div>
    </div>
  );
}

function AttachmentErrorCard({ item }: { item: AttachmentErrorRenderItem }) {
  return (
    <div
      style={{
        marginBottom: '4px',
        padding: '12px',
        background: '#fff7ed',
        border: '1px solid #fdba74',
        borderRadius: '8px',
        color: '#9a3412',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: '4px' }}>
        {item.fileName ? `Attachment unavailable: ${item.fileName}` : 'Attachment unavailable'}
      </div>
      <div style={{ overflowWrap: 'anywhere' }}>{item.error}</div>
    </div>
  );
}
